import React from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import './PlayerInfo.css';

interface PlayerInfoProps {
  anonName?: string | null;
}

/**
 * Truncate wallet address for display
 */
function truncateAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Shared PlayerInfo component for displaying player identity
 * Handles three states:
 * 1. Twitter user - Black badge with X logo + checkmark + handle (clickable)
 * 2. Wallet user - Black badge with checkmark + display name
 * 3. Not signed in - Blue badge with random name
 */
const PlayerInfo: React.FC<PlayerInfoProps> = ({ anonName }) => {
  const { isAuthenticated, user } = useAuthContext();

  // Condition 1: Signed in with Twitter
  if (isAuthenticated && user?.authType === 'twitter') {
    const handle = user.twitterHandle || user.displayName;
    return (
      <a
        href={`https://x.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="player-info twitter-user"
        style={{ textDecoration: 'none' }}
      >
        <img className="x-logo" src="/images/x-logo-black.png" alt="X" />
        <span className="verified-check">✓</span>
        {handle}
      </a>
    );
  }

  // Condition 2: Signed in with wallet
  if (isAuthenticated && user?.authType === 'wallet') {
    return (
      <div className="player-info wallet-user">
        <span className="verified-check">✓</span>
        {user.displayName || truncateAddress(user.walletAddress || '')}
      </div>
    );
  }

  // Condition 3: Not signed in - show random name
  if (anonName) {
    return (
      <div className="player-info custom-name">
        {anonName}
      </div>
    );
  }

  // Fallback: Anonymous
  return (
    <div className="player-info anon">
      anon
    </div>
  );
};

export default PlayerInfo;
